/**
 * Copyright (c) 2024-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { type CreateToasterReturn, Toaster, createToaster } from "@ark-ui/react/toast";
import { CloseLine } from "@ndla/icons";
import { IconButton, ToastCloseTrigger, ToastDescription, ToastRoot, ToastTitle } from "@ndla/primitives";
import { type ReactNode, createContext, useContext } from "react";

const toaster = createToaster({
  placement: "bottom",
  overlap: true,
  gap: 8,
  max: 10,
});

const ToastContext = createContext<CreateToasterReturn | null>(null);

interface ToastProviderProps {
  children?: ReactNode;
}

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  return (
    <ToastContext value={toaster}>
      <Toaster toaster={toaster}>
        {(toast) => (
          <ToastRoot>
            <ToastTitle>{toast.title}</ToastTitle>
            <ToastDescription>{toast.description}</ToastDescription>
            <ToastCloseTrigger asChild>
              <IconButton variant="clear">
                <CloseLine />
              </IconButton>
            </ToastCloseTrigger>
          </ToastRoot>
        )}
      </Toaster>
      {children}
    </ToastContext>
  );
};
